import { uniq } from 'es-toolkit';
import React from 'react';
import { List, ListItem, Typography } from '@mui/material';

import { ProductionComponentInstance } from '@paragon/contract-library';
import { getRanges, naturalSortByProperty, notNullish } from '@paragon/utilities';

import { getAnalysisSetGuidToComponentDesignName } from '../../store/selectors/getAnalysisSetGuidToComponentDesignName';
import { useAppSelector } from '../hooks';

interface ReplaceComponentsListProps {
  readonly existingTrussesWithSameName: { [newAnalysisSetGuid: string]: readonly string[] };
  readonly productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid: {
    [analysisSetGuid: string]: readonly ProductionComponentInstance[];
  };
}

export function ReplaceProductionComponentsList(props: ReplaceComponentsListProps) {
  const { existingTrussesWithSameName, productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid } = props;
  const bundles = useAppSelector((state) => state.bundles);
  const batches = useAppSelector((state) => state.batches);
  const analysisSetGuidToComponentDesignName = useAppSelector(getAnalysisSetGuidToComponentDesignName);
  const sortedExistingTrussesWithSameName = naturalSortByProperty(
    Object.entries(existingTrussesWithSameName),
    ([newAnalysisSetGuid]) => analysisSetGuidToComponentDesignName[newAnalysisSetGuid],
  );

  return (
    <List>
      {sortedExistingTrussesWithSameName.map(([newAnalysisSetGuid, oldAnalysisSetGuids]) => {
        const batchIdsAffectedByNewAnalysisSetGuid = uniq(
          oldAnalysisSetGuids
            .flatMap((oldAnalysisSetGuid) =>
              productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid[oldAnalysisSetGuid].map(
                (instance) => instance.batchGuid,
              ),
            )
            .filter(notNullish),
        )
          .map((batchGuid) => batches[batchGuid]?.id)
          .filter(notNullish);
        const bundleIdsAffectedByNewAnalysisSetGuid = uniq(
          oldAnalysisSetGuids
            .flatMap((oldAnalysisSetGuid) =>
              productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid[oldAnalysisSetGuid].map(
                (instance) => instance.bundleGuid,
              ),
            )
            .filter(notNullish),
        )
          .map((bundleGuid) => bundles[bundleGuid]?.id)
          .filter(notNullish);
        return (
          <ListItem key={newAnalysisSetGuid}>
            <Typography>{`${analysisSetGuidToComponentDesignName[newAnalysisSetGuid]}: Batch${
              batchIdsAffectedByNewAnalysisSetGuid.length === 1 ? '' : 'es'
            } ${getRanges(batchIdsAffectedByNewAnalysisSetGuid)} and Bundle${
              bundleIdsAffectedByNewAnalysisSetGuid.length === 1 ? '' : 's'
            } ${getRanges(bundleIdsAffectedByNewAnalysisSetGuid)}`}</Typography>
          </ListItem>
        );
      })}
    </List>
  );
}
